export type ScrollSource = 'grid' | 'header' | 'external'

interface HorizontalScrollSyncOptions {
  getGridLeft: () => number
  getHeaderLeft: () => number
  setGridLeft: (left: number) => void
  setHeaderLeft: (left: number) => void
  requestFrame?: (callback: FrameRequestCallback) => number
  cancelFrame?: (handle: number) => void
  scheduleTimeout?: (callback: () => void, delay: number) => number
  cancelTimeout?: (handle: number) => void
  settleDelay?: number
}

const SCROLL_EPSILON = 0.5

const isSamePosition = (first: number, second: number) =>
  Math.abs(first - second) <= SCROLL_EPSILON

export const createHorizontalScrollSync = ({
  getGridLeft,
  getHeaderLeft,
  setGridLeft,
  setHeaderLeft,
  requestFrame = window.requestAnimationFrame.bind(window),
  cancelFrame = window.cancelAnimationFrame.bind(window),
  scheduleTimeout = window.setTimeout.bind(window),
  cancelTimeout = window.clearTimeout.bind(window),
  settleDelay = 100
}: HorizontalScrollSyncOptions) => {
  let activeSource: ScrollSource | null = null
  let externalTargetLeft: number | null = null
  let verificationFrame: number | null = null
  let settleTimeout: number | null = null

  const cancelVerification = () => {
    if (verificationFrame == null) return
    cancelFrame(verificationFrame)
    verificationFrame = null
  }

  const cancelSettlement = () => {
    if (settleTimeout == null) return
    cancelTimeout(settleTimeout)
    settleTimeout = null
  }

  const setGridPosition = (left: number) => {
    if (!isSamePosition(getGridLeft(), left)) setGridLeft(left)
  }

  const setHeaderPosition = (left: number) => {
    if (!isSamePosition(getHeaderLeft(), left)) setHeaderLeft(left)
  }

  const getSourcePosition = (source: ScrollSource) => {
    if (source === 'grid') return getGridLeft()
    if (source === 'header') return getHeaderLeft()
    return externalTargetLeft ?? Math.min(getGridLeft(), getHeaderLeft())
  }

  const applySourcePosition = (source: ScrollSource, left: number) => {
    if (source !== 'grid') setGridPosition(left)
    if (source !== 'header') setHeaderPosition(left)
  }

  const convergeToReachablePosition = () => {
    const gridLeft = getGridLeft()
    const headerLeft = getHeaderLeft()
    if (isSamePosition(gridLeft, headerLeft)) return

    // The two surfaces can differ slightly at their maximum because of
    // browser rounding. The smaller position is reachable by both.
    const commonLeft = Math.min(gridLeft, headerLeft)
    setGridPosition(commonLeft)
    setHeaderPosition(commonLeft)
  }

  const verifyAndRelease = (source: ScrollSource) => {
    verificationFrame = null
    if (activeSource !== source) return

    applySourcePosition(source, getSourcePosition(source))
    convergeToReachablePosition()
    activeSource = null
    externalTargetLeft = null
  }

  const scheduleVerification = (source: ScrollSource) => {
    cancelVerification()
    verificationFrame = requestFrame(() => verifyAndRelease(source))
  }

  const endInteraction = (source: ScrollSource) => {
    if (activeSource !== source) return
    cancelSettlement()
    applySourcePosition(source, getSourcePosition(source))
    scheduleVerification(source)
  }

  const scheduleInteractionEnd = (source: ScrollSource) => {
    cancelSettlement()
    settleTimeout = scheduleTimeout(() => {
      settleTimeout = null
      endInteraction(source)
    }, settleDelay)
  }

  const beginInteraction = (source: ScrollSource) => {
    if (activeSource !== source) {
      cancelVerification()
      cancelSettlement()
      activeSource = source
      externalTargetLeft = null
    }

    if (source !== 'external') {
      applySourcePosition(source, getSourcePosition(source))
      scheduleInteractionEnd(source)
    }
  }

  const handleSourceScroll = (
    source: Exclude<ScrollSource, 'external'>,
    left: number
  ) => {
    if (activeSource == null) {
      const followerLeft =
        source === 'grid' ? getHeaderLeft() : getGridLeft()

      // An aligned event without an explicit input is normally a delayed
      // acknowledgement from the follower. It must not acquire ownership.
      if (isSamePosition(left, followerLeft)) return
      beginInteraction(source)
    }

    if (activeSource !== source) return

    cancelVerification()
    applySourcePosition(source, left)
    scheduleInteractionEnd(source)
  }

  const scrollTo = (left: number) => {
    cancelVerification()
    cancelSettlement()
    activeSource = 'external'
    externalTargetLeft = left
    applySourcePosition('external', left)
    scheduleVerification('external')
  }

  const reconcile = () => {
    cancelVerification()
    cancelSettlement()

    if (activeSource != null) {
      const source = activeSource
      applySourcePosition(source, getSourcePosition(source))
      scheduleVerification(source)
      return
    }

    const commonLeft = Math.min(getGridLeft(), getHeaderLeft())
    activeSource = 'external'
    externalTargetLeft = commonLeft
    applySourcePosition('external', commonLeft)
    scheduleVerification('external')
  }

  const ensureAligned = () => {
    cancelVerification()

    if (activeSource != null) {
      applySourcePosition(activeSource, getSourcePosition(activeSource))
    }
    convergeToReachablePosition()

    if (activeSource === 'external') {
      activeSource = null
      externalTargetLeft = null
    }
  }

  const dispose = () => {
    cancelVerification()
    cancelSettlement()
    activeSource = null
    externalTargetLeft = null
  }

  return {
    beginInteraction,
    dispose,
    endInteraction,
    ensureAligned,
    handleSourceScroll,
    reconcile,
    scrollTo
  }
}

export type HorizontalScrollSync = ReturnType<
  typeof createHorizontalScrollSync
>
